import React, { useEffect, useState } from "react";
import { Container, Card, Button, Row, Col, Form, Modal, Spinner, Dropdown } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import connectorService from "../services/connectorService";
import { userService } from "../services/userService";
import { FaEllipsisV, FaEdit, FaTrash } from "react-icons/fa";

const Connectors = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [showSftp, setShowSftp] = useState(true);
  const [mode, setMode] = useState("EXPORT");

  const [sftp, setSftp] = useState({
    name: "",
    host: "",
    port: "22",
    username: "",
    password: "",
    remoteDirectory: "",
  });

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configId, setConfigId] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfigId, setDeleteConfigId] = useState(null);
  const [deleteConfigName, setDeleteConfigName] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const roles = decoded.roles || [];
        setIsAdmin(roles.includes("ADMIN") || roles.includes("SUPERADMIN"));
        const username = decoded.sub;
        if (username) {
          userService.getUserByUsername(username).then((u) => {
            setAccountId(u.accountId || "");
          }).catch(() => {});
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    const fetchConfigs = async () => {
      if (!accountId) return;
      setConfigsLoading(true);
      try {
        const res = await connectorService.getAllConfigurations(accountId);
        const list = res?.data || res || [];
        setConfigs(Array.isArray(list) ? list : []);
      } catch (err) {
        setConfigs([]);
      } finally {
        setConfigsLoading(false);
      }
    };
    fetchConfigs();
  }, [accountId]);

  const handleSftpChange = (e) => {
    const { name, value } = e.target;
    setSftp((prev) => ({ ...prev, [name]: value }));
  };

  // Handle edit configuration - populate form with selected config
  const handleEditConfig = (cfg) => {
    setSftp({
      name: cfg.name || "",
      host: cfg.host || "",
      port: cfg.port?.toString() || "22",
      username: cfg.username || "",
      password: "", // Password should be re-entered for security
      remoteDirectory: cfg.remotePath || "",
    });
    setMode(cfg.configType?.toUpperCase() === "IMPORT" ? "IMPORT" : "EXPORT");
    setEditingConfigId(cfg.id);
    setConfigId(cfg.id);
    setIsEditMode(true);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info("Configuration loaded. Update the details and click Save.");
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setSftp({
      name: "",
      host: "",
      port: "22",
      username: "",
      password: "",
      remoteDirectory: "",
    });
    setMode("EXPORT");
    setEditingConfigId(null);
    setConfigId(null);
    setIsEditMode(false);
  };

  // Handle delete confirmation modal
  const handleDeleteClick = (cfg) => {
    setDeleteConfigId(cfg.id);
    setDeleteConfigName(`${cfg.username}@${cfg.host}`);
    setShowDeleteModal(true);
  };

  // Handle delete configuration
  const handleDeleteConfig = async () => {
    if (!deleteConfigId) return;
    
    setDeleting(true);
    try {
      await connectorService.deleteConfiguration(accountId, deleteConfigId);
      toast.success("Configuration deleted successfully");
      setShowDeleteModal(false);
      setDeleteConfigId(null);
      setDeleteConfigName("");
      
      // Refresh configs list
      const listRes = await connectorService.getAllConfigurations(accountId);
      const list = listRes?.data || listRes || [];
      setConfigs(Array.isArray(list) ? list : []);
      
      // If deleted config was being edited, reset form
      if (editingConfigId === deleteConfigId) {
        handleCancelEdit();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete configuration");
    } finally {
      setDeleting(false);
    }
  };


  const handleSaveConfig = async () => {
    if (!accountId) {
      toast.error("Account not found");
      return;
    }
    if (!sftp.host || !sftp.username || !sftp.password) {
      toast.error("Enter host, username, and password");
      return;
    }
    if (!sftp.name?.trim()) {
      toast.error("Enter a name for this configuration");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: sftp.name.trim(),
        host: sftp.host,
        port: parseInt(sftp.port || "22"),
        username: sftp.username,
        password: sftp.password,
        remotePath: sftp.remoteDirectory,
        configType: mode.toLowerCase(),
      };
      
      if (isEditMode && editingConfigId) {
        // Update existing configuration
        const res = await connectorService.updateConfiguration(accountId, editingConfigId, payload);
        
        // Check if response indicates failure
        if (res?.success === false) {
          toast.error(res?.message || "Failed to update SFTP configuration");
          return;
        }
        
        toast.success("SFTP configuration updated successfully");
        handleCancelEdit();
      } else {
        // Create new configuration
        const res = await connectorService.createConfiguration(accountId, payload);
        
        // Check if response indicates failure
        if (res?.success === false) {
          toast.error(res?.message || "Failed to save SFTP configuration");
          return;
        }
        
        const created = res?.data || res;
        setConfigId(created?.id || null);
        toast.success("SFTP configuration saved");
      }
      
      // Refresh configs list
      try {
        const listRes = await connectorService.getAllConfigurations(accountId);
        const list = listRes?.data || listRes || [];
        setConfigs(Array.isArray(list) ? list : []);
      } catch {}
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'save'} SFTP configuration`);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!configId) {
      toast.error("Save configuration first");
      return;
    }

    if(!sftp.password){
      toast.error("Enter Password");
      return;
    }
    setTesting(true);
    try {
      // await connectorService.updateConfiguration(accountId, configId, {
      //   host: sftp.host,
      //   port: parseInt(sftp.port || "22"),
      //   username: sftp.username,
      //   password: sftp.password,
      //   remotePath: sftp.remoteDirectory,
      //   configType: "import",
      // });
      const res = await connectorService.testConnection(accountId, configId);
      if (res?.success) {
        toast.success(res?.message || "Connection successful");
      } else {
        toast.error(res?.message || "Connection failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  

  return (
    <Container fluid className="ps-0 pb-4 pe-0 overflow-hidden bg-white">
      <Card className="card-nostyle">
        <Card.Body className="ps-4 pe-4 p-3 d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid #EAECF0" }}>
          <h4 className="mb-0 fs-20 poppins fw-600">Connectors</h4>
        </Card.Body>
      </Card>

      <Row className="p-4">
        <Col lg={12}>
          <Card className="border-0">
            <Card.Body>
              {/* <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-600">SFTP</h5>
                <div>
                  <Button variant={showSftp ? "success" : "outline-success"} onClick={() => setShowSftp(true)}>Open</Button>
                </div>
              </div> */}

              {showSftp && (
                <div>
                  <div className="mb-3">
                    <Form.Check inline type="radio" id="mode-export" label="Export" name="mode" value="EXPORT" checked={mode === "EXPORT"} onChange={(e) => setMode(e.target.value)} />
                    <Form.Check inline type="radio" id="mode-import" label="Import" name="mode" value="IMPORT" checked={mode === "IMPORT"} onChange={(e) => setMode(e.target.value)} />
                  </div>

                  <Row>
                    <Col lg={6}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-600 mb-0">Connection</h6>
                        {isEditMode && (
                          <span className="badge bg-warning text-dark">Editing Configuration</span>
                        )}
                      </div>
                      <Form.Group className="mb-2">
                        <Form.Label>Configuration Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control name="name" value={sftp.name} onChange={handleSftpChange} placeholder="e.g. Production SFTP, Backup Server" />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Host</Form.Label>
                        <Form.Control name="host" value={sftp.host} onChange={handleSftpChange} placeholder="sftp.example.com" />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Port</Form.Label>
                        <Form.Control name="port" value={sftp.port} onChange={handleSftpChange} placeholder="22" />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Username</Form.Label>
                        <Form.Control name="username" value={sftp.username} onChange={handleSftpChange} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Password {isEditMode && <span className="text-muted fs-12">(Re-enter to update)</span>}</Form.Label>
                        <Form.Control type="password" name="password" value={sftp.password} onChange={handleSftpChange} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Remote Directory</Form.Label>
                        <Form.Control name="remoteDirectory" value={sftp.remoteDirectory} onChange={handleSftpChange} placeholder="/exports" />
                      </Form.Group>

                      <div className="d-flex gap-2 mb-4">
                        <Button variant="success" onClick={handleSaveConfig} disabled={saving}>
                          {saving ? "Saving..." : (isEditMode ? "Update" : "Save")}
                        </Button>
                        {isEditMode && (
                          <Button variant="outline-secondary" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                        )}
                        <Button variant="outline-success" onClick={handleTestConnection} disabled={testing || !configId}>
                          {testing ? "Testing..." : "Test Connection"}
                        </Button>
                      </div>
                    </Col>

                    {/* {mode === "IMPORT" && (
                      <Col lg={6}>
                        <div>
                          <h6 className="fw-600 mb-2">Import Settings</h6>
                          <Form.Group className="mb-2">
                            <Form.Label>File Pattern</Form.Label>
                            <Form.Control name="filePattern" placeholder="*.csv" />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>Delimiter</Form.Label>
                            <Form.Control name="delimiter" placeholder="," />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Check type="checkbox" label="Header Row" />
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button variant="success" onClick={() => toast.success("Import settings saved")}>Save Import</Button>
                          </div>
                        </div>
                      </Col>
                    )} */}
                  </Row>
                </div>
              )}
              {showSftp && (
                <div className="mt-4">
                  <h6 className="fw-600 mb-2">Existing Configurations</h6>
                  {configsLoading ? (
                    <div className="py-3">Loading configurations...</div>
                  ) : configs.length === 0 ? (
                    <div className="py-3 text-muted">No configurations found</div>
                  ) : (
                    <div className="table_responsive w-100">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Host</th>
                            <th>Port</th>
                            <th>Username</th>
                            <th>Remote Path</th>
                            <th>TYPE</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {configs.map(cfg => (
                            <tr key={cfg.id} className={editingConfigId === cfg.id ? "table-warning" : ""}>
                              <td>{cfg.name || <span className="text-muted fst-italic">Unnamed</span>}</td>
                              <td>{cfg.host}</td>
                              <td>{cfg.port}</td>
                              <td>{cfg.username}</td>
                              <td>{cfg.remotePath}</td>
                              <td>
                                <span className={`badge ${cfg.configType === 'export' ? 'bg-00C76F text-white' : 'bg-info'}`}>
                                  {cfg.configType || '-'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${cfg.isActive ? 'bg-success' : 'bg-danger'}`}>
                                  {cfg.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    as="div"
                                    className="cursor-pointer p-2"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <FaEllipsisV className="text-secondary" />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu className="shadow-sm">
                                    <Dropdown.Item
                                      onClick={() => handleEditConfig(cfg)}
                                      className="d-flex align-items-center gap-2"
                                    >
                                      <FaEdit className="text-success" />
                                      <span>Update Configuration</span>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteClick(cfg)}
                                      className="d-flex align-items-center gap-2 text-danger"
                                    >
                                      <FaTrash />
                                      <span>Delete Configuration</span>
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => !deleting && setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="fs-18 fw-600">Delete Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <div className="mb-3">
              <FaTrash className="text-danger" style={{ fontSize: "48px" }} />
            </div>
            <p className="mb-2 fs-16">
              Are you sure you want to delete this SFTP configuration?
            </p>
            <p className="text-muted mb-0">
              <strong>{deleteConfigName}</strong>
            </p>
            <p className="text-danger fs-12 mt-2">
              This action cannot be undone. All scheduled exports using this configuration will also be affected.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfig}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Configuration"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </Container>
  );
};

export default Connectors;